import React, { useEffect, useState } from "react";
import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Pagination, 
  Card, 
  Container, 
  Row, 
  Col, 
  InputGroup, 
  FormControl, 
  Spinner, 
  Badge,
  Tooltip,
  OverlayTrigger
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";
import { FaSearch, FaEdit, FaTrash, FaEnvelope, FaExclamationTriangle } from "react-icons/fa";

const ContactsTable = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const contactsPerPage = 10;
  const pageLimit = 5; // Maximum number of pagination items to display at once

  useEffect(() => {
    const fetchContacts = async () => {
      setLoading(true);
      try {
        const contactsCollection = collection(db, "contacts");
        const snapshot = await getDocs(contactsCollection);
        const contactsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate?.() || new Date() // Convert Firestore timestamp to Date
        }));
        
        // Sort contacts by timestamp in descending order (newest first)
        contactsData.sort((a, b) => b.timestamp - a.timestamp);
        
        setContacts(contactsData);
        setFilteredContacts(contactsData);
      } catch (error) {
        console.error("Error fetching contacts: ", error);
        toast.error("Failed to fetch contacts. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  // Filter contacts based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = contacts.filter(contact => 
        (contact.name && contact.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.message && contact.message.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (contact.id && contact.id.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredContacts(filtered);
      setCurrentPage(1);
    } else {
      setFilteredContacts(contacts);
    }
  }, [searchTerm, contacts]);

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setFormData({
      name: contact.name || "",
      email: contact.email || "",
      message: contact.message || "",
    });
    setShowModal(true);
  };

  const handleShowDeleteConfirm = (contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const handleUpdate = async () => {
    try {
      // Basic validation
      if (!formData.name.trim() || !formData.email.trim()) {
        toast.error("Name and email are required.");
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email address.");
        return;
      }

      const contactRef = doc(db, "contacts", selectedContact.id);
      await updateDoc(contactRef, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      });
      
      // Update the local state to reflect the changes
      const updatedContacts = contacts.map((contact) =>
        contact.id === selectedContact.id
          ? { 
              ...contact, 
              name: formData.name.trim(),
              email: formData.email.trim(),
              message: formData.message.trim()
            }
          : contact
      );
      
      setContacts(updatedContacts);
      setShowModal(false);
      toast.success("Contact updated successfully.");
    } catch (error) {
      console.error("Error updating contact: ", error);
      toast.error("Failed to update contact. Please try again later.");
    }
  };

  const handleRemove = async () => {
    try {
      await deleteDoc(doc(db, "contacts", selectedContact.id));
      
      // Update the contacts list after deletion
      setContacts(contacts.filter((contact) => contact.id !== selectedContact.id));
      setFilteredContacts(filteredContacts.filter((contact) => contact.id !== selectedContact.id));
      
      setShowDeleteModal(false);
      toast.success("Contact removed successfully.");
    } catch (error) {
      console.error("Error removing contact: ", error);
      toast.error("Failed to remove contact. Please try again later.");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedContact(null);
    setFormData({
      name: "",
      email: "",
      message: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Get current contacts for pagination
  const indexOfLastContact = currentPage * contactsPerPage;
  const indexOfFirstContact = indexOfLastContact - contactsPerPage;
  const currentContacts = filteredContacts.slice(indexOfFirstContact, indexOfLastContact);
  const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);

  // Function to render a limited number of pagination items (sliding window)
  const renderPaginationItems = () => {
    let items = [];
    let startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2));
    let endPage = startPage + pageLimit - 1;
    
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - pageLimit + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  // Function to truncate long text
  const truncateText = (text, maxLength = 50) => {
    if (!text) return "N/A";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  // Function to format message for display in table
  const formatMessage = (message, maxLength = 60) => {
    if (!message) return "N/A";
    
    // Create a tooltip for longer messages
    if (message.length > maxLength) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{message}</Tooltip>}
        >
          <span>{truncateText(message, maxLength)}</span>
        </OverlayTrigger>
      );
    }
    
    return message;
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <h5 className="mb-0 font-weight-bold text-primary">
                    <FaEnvelope className="me-2" /> Contact Requests
                  </h5>
                  {loading ? (
                    <Spinner animation="border" size="sm" className="ms-3" />
                  ) : (
                    <Badge bg="info" className="ms-2">
                      {filteredContacts.length}
                    </Badge>
                  )}
                </div>
                
                <InputGroup style={{ maxWidth: "300px" }}>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <FormControl
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Card.Header>
              
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading contacts...</p>
                  </div>
                ) : filteredContacts.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="py-3" width="15%">Contact ID</th>
                          <th className="py-3" width="15%">Name</th>
                          <th className="py-3" width="20%">Email</th>
                          <th className="py-3" width="35%">Message</th>
                          <th className="py-3" width="15%">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentContacts.map((contact) => (
                          <tr key={contact.id}>
                            <td className="py-3">
                              <small className="text-muted">{truncateText(contact.id, 10)}</small>
                            </td>
                            <td className="py-3 fw-bold">{contact.name || "N/A"}</td>
                            <td className="py-3">
                              {contact.email ? (
                                <a href={`mailto:${contact.email}`} className="text-decoration-none">
                                  {contact.email}
                                </a>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td className="py-3">{formatMessage(contact.message)}</td>
                            <td className="py-3">
                              <div className="d-flex">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  className="me-2"
                                  onClick={() => handleEdit(contact)}
                                  title="Edit Contact"
                                >
                                  <FaEdit />
                                </Button>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={() => handleShowDeleteConfirm(contact)}
                                  title="Delete Contact"
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-5 empty-state">
                    <FaExclamationTriangle size={48} className="text-muted mb-3" />
                    <h5>No contacts found</h5>
                    <p className="text-muted">
                      {searchTerm ? "No contacts match your search criteria." : "There are no contact requests at this time."}
                    </p>
                  </div>
                )}
              </Card.Body>
              
              {filteredContacts.length > 0 && (
                <Card.Footer className="bg-white border-0 px-4 py-3">
                  <Row className="align-items-center">
                    <Col>
                      <small className="text-muted">
                        Showing {filteredContacts.length > 0 ? indexOfFirstContact + 1 : 0} to {Math.min(indexOfLastContact, filteredContacts.length)} of {filteredContacts.length} contacts
                      </small>
                    </Col>
                    <Col className="d-flex justify-content-end">
                      {totalPages > 1 && (
                        <Pagination className="mb-0">
                          <Pagination.First
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          {renderPaginationItems()}
                          <Pagination.Next
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      )}
                    </Col>
                  </Row>
                </Card.Footer>
              )}
            </Card>
          </Container>
        </main>
      </div>

      {/* Edit Contact Modal */}
      <Modal show={showModal} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formMessage">
              <Form.Label>Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                placeholder="Enter message"
                name="message"
                value={formData.message}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdate}>
            Update Contact
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3 text-danger">
            <FaTrash size={48} />
          </div>
          <h5>Are you sure you want to delete this contact?</h5>
          {selectedContact && (
            <p className="text-muted mb-0">
              Contact from: <strong>{selectedContact.name || "Unknown"}</strong>
              <br />
              Email: {selectedContact.email || "N/A"}
            </p>
          )}
          <p className="text-danger mt-3">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRemove}
          >
            Delete Contact
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContactsTable;